/**
 * Subscribes to live coding events for a work item — and, when it is told
 * which attempt to watch, for ONE run.
 *
 * On mount: fetches the historical event log from
 * GET /api/work-items/:id/coding-events (with `?runId=` when an attempt is
 * named).
 *
 * Then: subscribes to the workspace WS connection and appends every
 * incoming `coding_event` message whose `workItemId` matches — and, while an
 * attempt is being watched, whose `runId` matches it too.
 *
 * The run key is the fix for attempt bleed: coding events used to be keyed by
 * work item alone, so attempt 2's live pane replayed attempt 1's log and then
 * grew it with whatever was still broadcasting. A watcher of one attempt drops
 * a frame that names a different run AND a frame that names none, because an
 * unkeyed frame cannot prove it belongs to the attempt on screen. Nothing is
 * dropped when no attempt was asked for, which is the behaviour every existing
 * caller already relies on.
 *
 * Returns a chronologically-ordered array of CodingEvent records the
 * AgentThreadPane renders via CodingEventBlocks.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CodingEvent, WsServerMessage } from "@yagni/shared";
import { useWebSocket } from "./useWebSocket.js";

export interface UseRunEventsOptions {
  /** Test seam — overrides the REST backfill. */
  fetchInitial?: (workItemId: string, runId?: string | null) => Promise<CodingEvent[]>;
  /** Watch ONE attempt. Absent/null keeps the whole item's stream. */
  runId?: string | null;
  /**
   * The attempt is in motion: re-read the record on an interval as well as
   * listening. The events a supervised build records are written by the
   * Temporal worker process; they reach this socket through the backend's
   * cross-machine relay (`ws/relay.ts`), which is best effort and can be
   * switched off. Before it, the pane sat on "implement start" for the whole
   * build (2026-09-17). The record is the truth; while live, the pane keeps
   * reading it.
   */
  live?: boolean;
  /** Poll interval while live. */
  pollMs?: number;
  /**
   * The first read failed. The hook still returns `[]`, which every existing
   * caller renders as an empty log; a caller that states a fact about the run
   * from that emptiness ("recorded no events") listens here so a failed read
   * is never mistaken for an empty record.
   */
  onError?: (error: unknown) => void;
  /**
   * A read of the record landed, the first or a live poll's. Until the first
   * one does, the hook's `[]` is "not read yet", not "nothing recorded": a
   * caller that states a fact from the emptiness waits for this.
   */
  onRead?: () => void;
}

/** How often a live pane re-reads the record. */
export const LIVE_EVENTS_POLL_MS = 8_000;

/** When the newest event happened, or 0 for an empty record. */
function lastEventAt(events: readonly CodingEvent[]): number {
  const last = events[events.length - 1];
  return last && typeof last.at === "number" ? last.at : 0;
}

export function useRunEvents(
  workItemId: string,
  opts: UseRunEventsOptions = {},
): CodingEvent[] {
  const [events, setEvents] = useState<CodingEvent[]>([]);

  // REST backfill on mount or workItemId change. We bail out early when
  // workItemId is empty so a transient empty value during initial load
  // doesn't fire a malformed `/api/work-items//coding-events` request.
  const { fetchInitial, runId = null, live = false, pollMs = LIVE_EVENTS_POLL_MS, onError, onRead } =
    opts;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;
  useEffect(() => {
    if (!workItemId || !live) return;
    const fetcher = fetchInitial ?? defaultFetchInitial;
    let cancelled = false;
    let failedPolls = 0;
    const timer = setInterval(() => {
      fetcher(workItemId, runId).then(
        (latest) => {
          if (cancelled) return;
          failedPolls = 0;
          // The record only ever grows, and its newest event only ever moves
          // later; a read that is shorter AND no newer is a blip, never a
          // reason to blank what the reader is looking at.
          setEvents((prev) =>
            latest.length >= prev.length || lastEventAt(latest) > lastEventAt(prev) ? latest : prev,
          );
          onReadRef.current?.();
        },
        (error: unknown) => {
          if (cancelled) return;
          // A failed poll keeps the last good read, and says so once per
          // streak so a dead endpoint is not mistaken for a quiet run.
          failedPolls += 1;
          if (failedPolls === 1) {
            console.warn("[useRunEvents] live poll failed; keeping the last good read", error);
          }
        },
      );
    }, pollMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [workItemId, runId, fetchInitial, live, pollMs]);
  useEffect(() => {
    if (!workItemId) {
      setEvents([]);
      return;
    }
    const fetcher = fetchInitial ?? defaultFetchInitial;
    let cancelled = false;
    fetcher(workItemId, runId).then(
      (initial) => {
        if (cancelled) return;
        setEvents(initial);
        onReadRef.current?.();
      },
      (error: unknown) => {
        // The log stays empty; a caller that cares is told it was a failure.
        if (!cancelled) onErrorRef.current?.(error);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [workItemId, runId, fetchInitial]);

  // WS live subscription. Filter by workItemId so panes A and B don't
  // cross-contaminate each other's transcripts, and by runId so attempt 2's
  // pane never grows attempt 1's log.
  const onMessage = useCallback(
    (msg: WsServerMessage) => {
      if (msg.type !== "coding_event") return;
      if (msg.workItemId !== workItemId) return;
      if (runId && msg.runId !== runId) return;
      setEvents((prev) => [...prev, msg.event]);
    },
    [workItemId, runId],
  );
  useWebSocket(onMessage);

  return events;
}

async function defaultFetchInitial(
  workItemId: string,
  runId?: string | null,
): Promise<CodingEvent[]> {
  const query = runId ? `?runId=${encodeURIComponent(runId)}` : "";
  const res = await fetch(
    `/api/work-items/${encodeURIComponent(workItemId)}/coding-events${query}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`coding events read failed (HTTP ${res.status})`);
  const data = (await res.json()) as { events?: CodingEvent[] };
  return data.events ?? [];
}
